import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Box, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { getYouTubeThumbnail, getYouTubeEmbedUrl, extractYouTubeId } from '../../utils/youtubeHelper';

const SWIPE_THRESHOLD = 50;

/**
 * Rich media carousel for review cards.
 *
 * Image quality strategy:
 *   - Main (first) image: always loads full-quality `item.url` directly.
 *   - Non-main images: load `thumbnail_url` instantly; preload `item.url`
 *     in background and swap in when ready (progressive upgrade).
 *
 * Video strategy:
 *   - Shows YouTube thumbnail (with fallback to review image).
 *   - Clicking play embeds an inline YouTube iframe instead of navigating away.
 *   - Non-YouTube URLs open in a new tab.
 */
const ReviewMediaCarousel = ({ mediaItems = [], altText = '', height = 180, fallbackImage = null }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [playingVideoIdx, setPlayingVideoIdx] = useState(null);
  const [loadedFullUrls, setLoadedFullUrls] = useState(() => new Set());
  const [failedSrcs, setFailedSrcs] = useState(() => new Set());
  const touchStartX = useRef(null);

  const count = mediaItems.length;

  // Preload full-quality images for non-main items that have separate thumbnails
  useEffect(() => {
    mediaItems.forEach((item, idx) => {
      if (idx === 0 || item.type !== 'image') return;
      if (!item.thumbnail_url || !item.url || item.thumbnail_url === item.url) return;

      const img = new Image();
      img.onload = () => {
        setLoadedFullUrls(prev => {
          const next = new Set(prev);
          next.add(item.url);
          return next;
        });
      };
      img.src = item.url;
    });
  }, [mediaItems]);

  const goTo = useCallback((idx) => {
    const newIdx = Math.max(0, Math.min(idx, count - 1));
    setActiveIndex(newIdx);
    // Stop video when navigating away
    if (playingVideoIdx !== null && playingVideoIdx !== newIdx) {
      setPlayingVideoIdx(null);
    }
  }, [count, playingVideoIdx]);

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (touchStartX.current == null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > SWIPE_THRESHOLD) {
      goTo(activeIndex + (diff > 0 ? 1 : -1));
    }
    touchStartX.current = null;
  }, [activeIndex, goTo]);

  const handlePlayVideo = useCallback((idx) => {
    const item = mediaItems[idx];
    if (!item || item.type !== 'video') return;

    const videoId = extractYouTubeId(item.url);
    if (videoId) {
      // YouTube — play inline
      setPlayingVideoIdx(idx);
    } else {
      // Non-YouTube — open in new tab
      window.open(item.url, '_blank', 'noopener');
    }
  }, [mediaItems]);

  const handleImgError = useCallback((src) => {
    if (src) {
      setFailedSrcs(prev => {
        const next = new Set(prev);
        next.add(src);
        return next;
      });
    }
  }, []);

  /**
   * Determine the display image src for a given item + index.
   *
   * Video thumbnail fallback chain:
   *   1. YouTube video thumbnail (hqdefault) — most relevant for video content
   *   2. YouTube video thumbnail (mqdefault) — if hq failed to load
   *   3. Review's own image (first image-type item in mediaItems)
   *   4. fallbackImage prop (eventImage from PlaceDetails)
   *   5. null → renders styled gradient placeholder
   */
  const getDisplaySrc = useCallback((item, idx) => {
    if (item.type === 'video') {
      // 1. YouTube thumbnail HQ — directly relevant to the video
      const ytThumbHq = getYouTubeThumbnail(item.url, 'hq');
      if (ytThumbHq && !failedSrcs.has(ytThumbHq)) return ytThumbHq;

      // 2. YouTube thumbnail MQ — fallback if HQ unavailable
      const ytThumbMq = getYouTubeThumbnail(item.url, 'mq');
      if (ytThumbMq && !failedSrcs.has(ytThumbMq)) return ytThumbMq;

      // 3. Review's own image (if YouTube thumbnail failed/unavailable)
      const firstImg = mediaItems.find(m => m.type === 'image');
      if (firstImg?.url && !failedSrcs.has(firstImg.url)) return firstImg.url;

      // 4. fallbackImage prop
      if (fallbackImage && !failedSrcs.has(fallbackImage)) return fallbackImage;

      // 5. Nothing available → null triggers styled placeholder
      return null;
    }

    // Main image (index 0): always full quality
    if (idx === 0) return item.url;

    // Non-main images: show thumbnail until full quality is loaded
    if (item.thumbnail_url && item.url !== item.thumbnail_url) {
      return loadedFullUrls.has(item.url) ? item.url : item.thumbnail_url;
    }
    return item.url;
  }, [mediaItems, loadedFullUrls, failedSrcs, fallbackImage]);

  if (!count) return null;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height,
        overflow: 'hidden',
        borderTopLeftRadius: 3,
        borderTopRightRadius: 3,
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Slides container */}
      <Box
        sx={{
          display: 'flex',
          width: `${count * 100}%`,
          height: '100%',
          transform: `translateX(-${activeIndex * (100 / count)}%)`,
          transition: 'transform 0.3s ease',
        }}
      >
        {mediaItems.map((item, idx) => {
          const isVideo = item.type === 'video';
          const isPlaying = playingVideoIdx === idx;

          return (
            <Box
              key={idx}
              sx={{
                width: `${100 / count}%`,
                height: '100%',
                flexShrink: 0,
                position: 'relative',
                cursor: isVideo && !isPlaying ? 'pointer' : 'default',
              }}
              onClick={isVideo && !isPlaying ? () => handlePlayVideo(idx) : undefined}
            >
              {isPlaying ? (
                /* Inline YouTube player */
                <Box
                  component="iframe"
                  src={`${getYouTubeEmbedUrl(item.url)}?autoplay=1`}
                  allow="autoplay; encrypted-media"
                  allowFullScreen
                  frameBorder="0"
                  sx={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                />
              ) : (() => {
                const imgSrc = getDisplaySrc(item, idx);
                return imgSrc ? (
                  <>
                    <Box
                      component="img"
                      src={imgSrc}
                      alt={`${altText} ${idx + 1}`}
                      loading={idx === 0 ? 'eager' : 'lazy'}
                      onError={() => handleImgError(imgSrc)}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                    {isVideo && (
                      <PlayCircleOutlineIcon
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          fontSize: 56,
                          color: 'rgba(255,255,255,0.9)',
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                          pointerEvents: 'none',
                        }}
                      />
                    )}
                  </>
                ) : (
                  /* Styled placeholder when no thumbnail is available */
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    }}
                  >
                    <PlayCircleOutlineIcon
                      sx={{
                        fontSize: 64,
                        color: 'rgba(255,255,255,0.95)',
                        filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))',
                        mb: 0.5,
                      }}
                    />
                    <Box
                      component="span"
                      sx={{
                        color: 'rgba(255,255,255,0.85)',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        letterSpacing: 0.5,
                      }}
                    >
                      Play Video
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          );
        })}
      </Box>

      {/* Arrow buttons — always visible when multiple images */}
      {count > 1 && (
        <>
          {activeIndex > 0 && (
            <IconButton
              size="small"
              onClick={() => goTo(activeIndex - 1)}
              sx={{
                position: 'absolute',
                top: '50%',
                left: 4,
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.45)',
                color: '#fff',
                zIndex: 2,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
                width: 30,
                height: 30,
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          )}
          {activeIndex < count - 1 && (
            <IconButton
              size="small"
              onClick={() => goTo(activeIndex + 1)}
              sx={{
                position: 'absolute',
                top: '50%',
                right: 4,
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.45)',
                color: '#fff',
                zIndex: 2,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
                width: 30,
                height: 30,
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          )}
        </>
      )}

      {/* Dot indicators */}
      {count > 1 && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 6,
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: 0.6,
          }}
        >
          {mediaItems.map((_, idx) => (
            <Box
              key={idx}
              onClick={() => goTo(idx)}
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: idx === activeIndex ? '#fff' : 'rgba(255,255,255,0.5)',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                boxShadow: '0 1px 2px rgba(0,0,0,0.4)',
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default React.memo(ReviewMediaCarousel);
